import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Wrench, FileText, Radio, Shield } from "lucide-react";

import { useSites } from "../lib/useSites";
import type { Site } from "../lib/types";

type Item = {
  id: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  action: () => void;
};

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const sites = useSites();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const items: Item[] = useMemo(() => {
    const ql = q.toLowerCase().trim();
    const siteItems: Item[] = sites
      .filter((s) => !ql || s.code.toLowerCase().includes(ql) || (s.address ?? "").toLowerCase().includes(ql))
      .slice(0, 12)
      .map((s: Site) => ({
        id: `site-${s.code}`,
        label: `${s.code}`,
        hint: `${s.country} · ${s.bu} · ${s.site_type}${s.repeaters ? ` · ${s.repeaters} repeaters` : ""}`,
        icon: <Radio className="h-4 w-4 text-zinc-500" />,
        action: () => {
          navigate(`/sites/${s.code}`);
          onClose();
        },
      }));

    const actions: Item[] = [
      {
        id: "act-freq-exp",
        label: "Find expired frequencies",
        hint: "Filter: valid_until < today",
        icon: <FileText className="h-4 w-4 text-amber-400" />,
        action: () => onClose(),
      },
      {
        id: "act-r7",
        label: "Provision new R7 for site…",
        hint: "Generate codeplug + AES key + QR",
        icon: <Wrench className="h-4 w-4 text-emerald-400" />,
        action: () => onClose(),
      },
      {
        id: "act-audit",
        label: "Generate Audit Pack",
        hint: "ZIP for AMZ Infosec",
        icon: <Shield className="h-4 w-4 text-zinc-500" />,
        action: () => onClose(),
      },
    ];

    if (!ql) return [...actions, ...siteItems];
    const filteredActions = actions.filter((a) => a.label.toLowerCase().includes(ql));
    return [...filteredActions, ...siteItems];
  }, [q, sites, navigate, onClose]);

  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [q]);

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      items[active]?.action();
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:pt-24 bg-zinc-950/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg border border-zinc-800 bg-zinc-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 border-b border-zinc-800">
          <Search className="h-4 w-4 text-zinc-500" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search 234 sites, run command, find asset…"
            className="flex-1 bg-transparent outline-none py-3 text-sm placeholder:text-zinc-600"
          />
          <kbd className="text-[10px] font-mono text-zinc-600 border border-zinc-800 rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>
        <div className="max-h-80 overflow-auto py-1">
          {items.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-zinc-500">No matches.</div>
          )}
          {items.map((it, i) => (
            <button
              type="button"
              key={it.id}
              onClick={it.action}
              onMouseEnter={() => setActive(i)}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm ${
                i === active ? "bg-zinc-800/60" : "hover:bg-zinc-800/30"
              }`}
            >
              {it.icon}
              <div className="flex-1 min-w-0">
                <div className="font-mono text-zinc-100">{it.label}</div>
                {it.hint && <div className="text-xs text-zinc-500 truncate">{it.hint}</div>}
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-zinc-700" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
